import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { Avatar } from '../avatar/avatar';
import { ZagIcon } from '../icon/icon';
import { TooltipDirective } from '../tooltip/tooltip.directive';

@Component({
  selector: 'zag-selection-row',
  imports: [Avatar, ZagIcon, TooltipDirective],
  template: `
    <label
      class="zag-selection-row"
      [class.zag-selection-row--selected]="checked()"
      [class.zag-selection-row--changed]="changed()"
      [class.zag-selection-row--disabled]="disabled()"
      [class.zag-selection-row--check-end]="checkPosition() === 'end'"
    >
      <input
        type="checkbox"
        class="zag-selection-row__checkbox"
        [checked]="checked()"
        [disabled]="disabled()"
        [attr.aria-describedby]="describedById()"
        (change)="onCheckedChange($event)"
      />
      <span class="zag-selection-row__check">
        @if (checked()) {
          <span zagIcon="check"></span>
        }
      </span>
      @if (avatarSrc() || avatarName()) {
        <zag-avatar [src]="avatarSrc()" [name]="avatarName()" size="sm" />
      }
      <span class="zag-selection-row__info">
        <span class="zag-selection-row__label">{{ label() }}</span>
        @if (description()) {
          <span class="zag-selection-row__description">{{ description() }}</span>
        }
      </span>
      @if (conflictHint()) {
        <span class="zag-selection-row__conflict" [zagTooltip]="conflictHint()!" tooltipPosition="above" [attr.aria-label]="conflictHint()">
          <span zagIcon="warning"></span>
        </span>
      }
    </label>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
      }

      .zag-selection-row__checkbox {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }

      .zag-selection-row {
        position: relative;
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        width: 100%;
        box-sizing: border-box;
        padding: var(--spacing-sm) var(--spacing-md);
        background-color: var(--surface-color-default);
        border: 1px solid var(--border-color-default);
        border-radius: var(--border-radius-md);
        cursor: pointer;
        transition: all var(--transition-duration-fast) var(--transition-timing);
      }

      .zag-selection-row:hover {
        background-color: var(--surface-color-hover);
        border-color: var(--color-primary);
      }

      .zag-selection-row:has(.zag-selection-row__checkbox:focus-visible) {
        outline: 2px solid var(--color-primary);
        outline-offset: 2px;
      }

      .zag-selection-row--selected {
        background-color: rgba(74, 144, 226, 0.1);
        border-color: var(--color-primary);
      }

      .zag-selection-row--selected .zag-selection-row__check {
        color: var(--color-primary);
      }

      .zag-selection-row--changed {
        border-color: var(--color-warning);
        background-color: var(--color-warning-subtle);
      }

      .zag-selection-row--disabled {
        cursor: not-allowed;
        opacity: 0.55;
      }

      .zag-selection-row--disabled:hover {
        background-color: var(--surface-color-default);
        border-color: var(--border-color-default);
      }

      .zag-selection-row__conflict {
        display: flex;
        align-items: center;
        flex-shrink: 0;
        color: var(--color-warning);
        cursor: help;
      }

      .zag-selection-row__conflict [zagIcon] {
        font-size: 20px;
      }

      .zag-selection-row__check {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        flex-shrink: 0;
        color: transparent;
      }

      .zag-selection-row__check [zagIcon] {
        font-size: 20px;
      }

      .zag-selection-row--check-end .zag-selection-row__check {
        order: 10;
      }

      .zag-selection-row__info {
        display: flex;
        flex-direction: column;
        gap: 2px;
        flex: 1;
        min-width: 0;
      }

      .zag-selection-row__label {
        font-weight: 500;
        color: var(--text-color-primary);
      }

      .zag-selection-row__description {
        font-size: var(--font-size-sm);
        color: var(--text-color-secondary);
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectionRowComponent {
  readonly label = input.required<string>();
  readonly description = input<string | null>(null);
  /** Optional avatar image URL. When set (or avatarName), a leading zag-avatar renders. */
  readonly avatarSrc = input<string | null>(null);
  /** Name for avatar initials fallback. */
  readonly avatarName = input<string>('');
  readonly checked = input(false);
  readonly changed = input(false);
  /** Checkbox mark position: 'start' keeps today's layout, 'end' right-aligns the mark. */
  readonly checkPosition = input<'start' | 'end'>('start');
  /** When true the row is greyed out and the checkbox is not interactive. */
  readonly disabled = input(false);
  /** When set, a warning icon is shown with this text as tooltip. */
  readonly conflictHint = input<string | null>(null);
  /**
   * Id of an element that describes the row's meaning — bound to the
   * underlying checkbox as `aria-describedby`. Used by the solver
   * settings dialog to tie the `maxCoverage` advisory to its toggle
   * (AC11 in docs/specs/auto-fill-solver-correctness.md).
   */
  readonly describedById = input<string | null>(null);

  readonly checkedChange = output<boolean>();

  onCheckedChange(event: Event): void {
    const isChecked = (event.target as HTMLInputElement | null)?.checked ?? false;
    this.checkedChange.emit(isChecked);
  }
}
